import {
  calculateGetItBy,
  calculateSoonestAvailable
} from '@carvana/merch-tile';
import cx from 'classnames';
import { useContext } from 'react';
import Skeleton from '@/common/helios/Skeleton';
import ShippingTo from '@/vdp/VehicleInfo/ShippingTo';
import TransitInfo from '~/components/common/TransitInfo';
import TransitChargeState from '~/constants/common/TransitChargeState';
import DeliveryPickupContext, {
  useDeliveryInfo,
  usePickupInfo
} from '~/context/vdp/DeliveryPickupContext';
import UserVehicleContext from '~/context/vdp/UserVehicleContext';
import useIsAvailabilityDisabled from '~/hooks/vdp/selectors/useIsAvailabilityDisabled';
import useIsNotAvailableForImmediatePurchase from '~/hooks/vdp/selectors/useIsNotAvailableForImmediatePurchase';
import useTransitChargeInfo from '~/hooks/vdp/selectors/useTransitChargeInfo';
import { DetailText, Separator } from './Terms/Terms.styles';
import useIsInZipExperiment from './useIsInZipExperiment';

export default function ShippingAndGetItBy() {
  const isAvailabilityDisabled = useIsAvailabilityDisabled();
  const transitCharge = useTransitChargeInfo();
  const { deliveryPickup } = useContext(DeliveryPickupContext);
  const { vehicleAvailability } = useContext(UserVehicleContext);
  const delivery = useDeliveryInfo();
  const isAvailableForImmediatePurchase =
    !useIsNotAvailableForImmediatePurchase();
  const pickup = usePickupInfo();
  const isInZipExperiment = useIsInZipExperiment();

  const soonestAvailable = calculateSoonestAvailable({ delivery, pickup });
  const { getItBy } = calculateGetItBy(soonestAvailable, {
    ordinalDay: false,
    sentenceCase: true,
    truncateDay: true
  });
  const soonestDays = Number(soonestAvailable.daysToAvailability);
  const [getItByDay, getItByDate] = getItBy.split(',');

  const showGetItByInfo = Boolean(getItBy) && !isAvailabilityDisabled;
  const isTransitChargeLoading =
    isAvailableForImmediatePurchase && !transitCharge.isLoaded;
  const hasTransitCharge =
    transitCharge.state !== TransitChargeState.Unknown ||
    isTransitChargeLoading;

  const isLoading =
    (isAvailableForImmediatePurchase &&
      (!deliveryPickup.isLoaded || !vehicleAvailability.isLoaded)) ||
    isTransitChargeLoading;

  const showTransitInfo =
    !isAvailabilityDisabled && hasTransitCharge && !isInZipExperiment;
  const canShow = showGetItByInfo || showTransitInfo || isInZipExperiment;

  if (isLoading) return <Skeleton className="my-sm" height={16} width={215} />;

  return canShow ? (
    <div className="flex flex-col gap-xs">
      <DetailText>
        {showTransitInfo && <TransitInfo transitCharge={transitCharge} />}
        {showGetItByInfo && showTransitInfo && (
          <Separator className="bg-text-default" />
        )}
        {showGetItByInfo && (
          <div data-testid="vehicle-info-card-delivery-pickup">
            <span
              className={cx('t-label-sm', {
                '!font-semibold': soonestDays < 4
              })}
            >
              Get it {getItByDay}
            </span>
            {getItByDate ? `, ${getItByDate}` : ''}
          </div>
        )}
        {isInZipExperiment && <ShippingTo hideDot={!showGetItByInfo} />}
      </DetailText>
    </div>
  ) : null;
}
